import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Drawer, Stack, Typography } from '@mui/material';
import { useLSContext } from '../Context/useLSContext';
import { useDataContext } from '../Context/useDataContext';
import { useHoleSettings } from '../Context/useHoleSettingsContext';
import { ChangeHolePopup } from './ChangeHolePopup';
import { OpponentScoringHBH } from './OpponentScoringHBH';
import { Challenges } from './Challenges';
import { ToolsTab } from '../Hooks/useLSStates';

interface ToolsDrawerProps {
    /** Optional override for admin/test previews that are outside the shared tools state. */
    open?: boolean;
    setOpen?: (open: boolean) => void;
}

export const ToolsDrawer = ({ open: openProp, setOpen: setOpenProp }: ToolsDrawerProps) => {
    const {
        toolsOpen,
        setToolsOpen,
        toolsTab,
        setToolsTab,
    } = useLSContext();
    const { eventData, currentRound, scoreData } = useDataContext();
    const { holeSettings } = useHoleSettings();

    const open = openProp ?? toolsOpen;
    const setOpen = setOpenProp ?? setToolsOpen;
    const [localTab, setLocalTab] = useState<ToolsTab>('changehole');
    const activeTab = openProp !== undefined ? localTab : toolsTab;
    const setActiveTab = openProp !== undefined ? setLocalTab : setToolsTab;

    const [activeRound, setActiveRound] = useState<any>(null);

    useEffect(() => {
        if (eventData && Object.keys(eventData).length > 0) {
            const round = eventData.rounds?.find((r: any) => r.roundNumber === currentRound);
            setActiveRound(round);
        }
    }, [eventData, currentRound]);

    const hasChallenges = useMemo(() => {
        return (activeRound?.challenges?.length ?? 0) > 0;
    }, [activeRound]);

    const currentChallenge = useMemo(() => {
        return activeRound?.challenges?.find((c: any) => c.hole === holeSettings.hole);
    }, [activeRound, holeSettings.hole]);

    const tabs: { id: ToolsTab; label: string }[] = [
        { id: 'changehole', label: 'Change Hole' },
        { id: 'challenges', label: 'Challenges' },
        { id: 'scorecard', label: 'Scorecard' },
    ];

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={() => setOpen(false)}
            PaperProps={{
                sx: {
                    height: '70dvh',
                    borderRadius: '16px 16px 0 0',
                    backgroundColor: 'sepia.background',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {/* Drag handle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5, flexShrink: 0 }}>
                <Box sx={{
                    width: 40,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: 'sepia.light',
                    opacity: 0.7,
                }} />
            </Box>

            {/* Chip tabs */}
            <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                sx={{ px: 3, pt: 1, pb: 1.5, flexShrink: 0 }}
            >
                {tabs.map((tab) => {
                    const isChallengeChip = tab.id === 'challenges' && !!currentChallenge;
                    const isActive = activeTab === tab.id;
                    return (
                        <Chip
                            key={tab.id}
                            label={tab.label}
                            onClick={() => setActiveTab(tab.id)}
                            sx={{
                                fontWeight: isActive ? 700 : 400,
                                backgroundColor: isActive
                                    ? isChallengeChip ? 'rgb(184, 115, 51)' : 'primary.light'
                                    : isChallengeChip ? 'rgba(184, 115, 51, 0.10)' : 'white',
                                color: isActive ? 'white'
                                    : isChallengeChip ? 'rgb(140, 80, 20)' : 'text.primary',
                                border: '1px solid',
                                borderColor: isActive
                                    ? isChallengeChip ? 'rgb(184, 115, 51)' : 'primary.light'
                                    : isChallengeChip ? 'rgba(184, 115, 51, 0.6)' : 'divider',
                                ...(isChallengeChip && {
                                    boxShadow: '0 0 8px 2px rgba(184, 115, 51, 0.5)',
                                }),
                                '&:hover': {
                                    backgroundColor: isActive
                                        ? isChallengeChip ? 'rgb(160, 95, 30)' : 'primary.main'
                                        : isChallengeChip ? 'rgba(184, 115, 51, 0.20)' : 'grey.100',
                                },
                            }}
                        />
                    );
                })}
            </Stack>

            {/* Tab content */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 3 }}>

                {activeTab === 'changehole' && (
                    scoreData?.length > 0 ? (
                        <Stack direction="column" spacing={2} alignItems="center" sx={{ pt: 2, width: '100%' }}>
                            <ChangeHolePopup changeHoleOpen={true} setChangeHoleOpen={setOpen} />
                        </Stack>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                            <Typography variant="body2" sx={{ color: 'sepia.light' }}>
                                Load a scoring session to use tools
                            </Typography>
                        </Box>
                    )
                )}

                {activeTab === 'scorecard' && (
                    <Box sx={{ pt: 1 }}>
                        <OpponentScoringHBH allowEdit={false} factor={1} holeSettings={holeSettings} />
                    </Box>
                )}

                {activeTab === 'challenges' && (
                    hasChallenges ? (
                        <Box sx={{ pt: 2 }}>
                            <Challenges challenge={currentChallenge} />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                            <Typography variant="body2" sx={{ color: 'sepia.light' }}>
                                No challenges for this round
                            </Typography>
                        </Box>
                    )
                )}
            </Box>
        </Drawer>
    );
};
